import { ThemeProvider, createTheme } from "@mui/material/styles";
import CircularProgress from "@mui/material/CircularProgress";
import "bootstrap/dist/css/bootstrap.css";
import { Route, Switch } from "react-router-dom";
import "./Assets/Styles/app.css";
import Footer from "./Components/Footer/Footer";
import Header from "./Components/Header/Header";
import { routes } from "./routes";
import { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import NotFound from "./Pages/NotFound/notFound";
import DisableZoom from "./Components/DisableZoom/DisableZoom";
import { useLocation } from "react-router-dom";
import {
  setCreate_Template,
  setDiscarded,
  setEdit_Template,
  setTemplate_Edit,
} from "./Redux/UserSlice/userSlice";
import { ClearImage } from "./Redux/MainImage/imageSlice";
import { deleteAll } from "./Redux/WaterMarks/watermarks";
import React from "react";
import type { RouteConfig } from "./routes";
import UploadedFontManager from "./Utils/uploadedFontManager";
// import GoogleLoginFB from "./Components/GoogleLoginFB/GoogleLoginFB";
// import { auth } from "./Config/firebase-config";

const theme = createTheme({
  palette: {
    secondary: {
      main: "#f25665",
    },
  },
});

const FallbackLoader = () => (
  <div className="circular-progress-bar">
    <CircularProgress color="secondary" />
  </div>
);

if (typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent)) {
  document.body.classList.add("firefox");
}
// The browser's own scroll restoration can race with (and silently win over) an explicit
// scrollTo below, re-applying the previous page's scroll position after a pushState
// navigation. Taking manual control here so route changes always land at the top.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}
function App(): React.ReactElement {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const dispatch = useDispatch();
  const location = useLocation();

  useEffect(() => {
    // If the full React app is ever loaded inside an iframe (e.g. from an unhandled iframe navigation),
    // break out of the iframe so we never render duplicate headers/footers.
    if (typeof window !== "undefined" && window.self !== window.top) {
      try {
        if (window.top) {
          window.top.location.href = window.location.href;
        }
      } catch {
        // Cross-origin fallback
      }
    }

    // Load and dynamically register all custom fonts from IndexedDB into document.fonts
    void UploadedFontManager.loadAndRegisterAllUploadedFonts();
  }, []);

  // Runs on every route change (this effect's dependency is the whole `location` object, so
  // it re-fires even between routes that happen to share a component). Centralizing the
  // scroll-to-top here instead of relying only on each page's own mount effect.
  // useLayoutEffect (not useEffect) so the reset happens synchronously BEFORE the browser
  // paints the new route -- otherwise the page flashes at the old scroll offset first. Reset
  // all three scroll roots since which one actually scrolls varies by browser/layout, and add
  // a post-paint rAF pass to catch any async layout shift (late image loads) nudging it back.
  useLayoutEffect(() => {
    const toTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    toTop();
    const raf = requestAnimationFrame(toTop);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/edit") {
      void Promise.all([
        dispatch(setTemplate_Edit(false)),
        dispatch(setCreate_Template(false)),
        // EWW-456: Header's save-success block no longer clears the authoring flags (the /edit
        // teardown redirect reads them to decide it should return to /addtemplate), so leaving
        // /edit is now the single place both get cleared -- otherwise `edit_Template` would stay
        // set after an update-template save and mis-flag the next editing session.
        dispatch(setEdit_Template(false)),
        dispatch(ClearImage()),
        dispatch(deleteAll()),
        dispatch(setDiscarded(false)),
      ]);
    }
  }, [location, dispatch]);

  const getRoutes = (route: RouteConfig[] | undefined) => {
    return route?.map((item, i) => <Route exact {...item} key={i} />);
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="App">
        <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
        {/* <GoogleLoginFB auth={ } /> */}
        <React.Suspense fallback={<FallbackLoader />}>
          <Switch>
            {getRoutes(routes)}
            <Route component={NotFound} />
          </Switch>
        </React.Suspense>
        <Footer />
        <DisableZoom />
      </div>
    </ThemeProvider>
  );
}

export default App;
